/**
 * @fileoverview Custom hook for managing app bar drawers and menus state.
 * Consolidates multiple useState calls and provides symmetric operations.
 */

import { useCallback, useState } from 'react';

interface AppBarState {
  bookmarksOpen: boolean;
  tagsOpen: boolean;
  settingsAnchor: HTMLElement | null;
}

interface AppBarStateActions {
  // Bookmark operations
  openBookmarks: () => void;
  closeBookmarks: () => void;
  toggleBookmarks: () => void;
  // Tags operations
  openTags: () => void;
  closeTags: () => void;
  toggleTags: () => void;
  // Settings menu operations
  openSettings: (event: React.MouseEvent<HTMLElement>) => void;
  closeSettings: () => void;
}

export interface UseAppBarStateReturn extends AppBarState, AppBarStateActions {}

/**
 * Manages app bar drawer and menu state with symmetric operations.
 * Ensures only one drawer is open at a time.
 */
export function useAppBarState(): UseAppBarStateReturn {
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);

  // Bookmark operations (closes tags when opening for mutual exclusion)
  const openBookmarks = useCallback(() => {
    setTagsOpen(false);
    setBookmarksOpen(true);
  }, []);

  const closeBookmarks = useCallback(() => {
    setBookmarksOpen(false);
  }, []);

  const toggleBookmarks = useCallback(() => {
    setBookmarksOpen((prev) => {
      if (!prev) setTagsOpen(false);
      return !prev;
    });
  }, []);

  // Tags operations (closes bookmarks when opening for mutual exclusion)
  const openTags = useCallback(() => {
    setBookmarksOpen(false);
    setTagsOpen(true);
  }, []);

  const closeTags = useCallback(() => {
    setTagsOpen(false);
  }, []);

  const toggleTags = useCallback(() => {
    setTagsOpen((prev) => {
      if (!prev) setBookmarksOpen(false);
      return !prev;
    });
  }, []);

  // Settings menu operations
  const openSettings = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsAnchor(null);
  }, []);

  return {
    // State
    bookmarksOpen,
    tagsOpen,
    settingsAnchor,
    // Actions
    openBookmarks,
    closeBookmarks,
    toggleBookmarks,
    openTags,
    closeTags,
    toggleTags,
    openSettings,
    closeSettings,
  };
}
